import PlanDto from './PlanDto';

export type BILLING_PERIOD = 1 | 2 | 3 | 6 | 12 | 24;

export enum PAYMENT_METHOD {
    CREDIT_CARD = 'CREDIT_CARD',
    WIRE_TRANSFER = 'WIRE_TRANSFER',
}

export enum STATUS {
    FUTURE = 'FUTURE',
    IN_TRIAL = 'IN_TRIAL',
    ACTIVE = 'ACTIVE',
    NON_RENEWING = 'NON_RENEWING',
    PAUSED = 'PAUSED',
    CANCELLED = 'CANCELLED',
    _UNKNOWN = '_UNKNOWN',
}

export enum CancelReason {
    NOT_PAID = 'NOT_PAID',
    NO_CARD = 'NO_CARD',
    FRAUD_REVIEW_FAILED = 'FRAUD_REVIEW_FAILED',
    NON_COMPLIANT_EU_CUSTOMER = 'NON_COMPLIANT_EU_CUSTOMER',
    TAX_CALCULATION_FAILED = 'TAX_CALCULATION_FAILED',
    CURRENCY_INCOMPATIBLE_WITH_GATEWAY = 'CURRENCY_INCOMPATIBLE_WITH_GATEWAY',
    NON_COMPLIANT_CUSTOMER = 'NON_COMPLIANT_CUSTOMER',
    _UNKNOWN = '_UNKNOWN',
}

export enum CustomerState {
    /** A customer still on freshbooks */
    FRESHBOOKS = 'FRESHBOOKS',
    /** A customer in trial */
    TRIAL = 'TRIAL',
    /** A customer in trial that has completed the checkout process */
    TRIAL_CHECKED_OUT = 'TRIAL_CHECKED_OUT',
    /** A customer cancels during trial */
    TRIAL_NON_RENEWING = 'TRIAL_NON_RENEWING',
    /** A customer in future state */
    FUTURE = 'FUTURE',
    /** A customer in future state that has completed the checkout process */
    FUTURE_CHECKED_OUT = 'FUTURE_CHECKED_OUT',
    /** A regular active customer, has CC or autocollection off === has checked out*/
    ACTIVE = 'ACTIVE',
    /** A free user, active but no addons */
    ACTIVE_FREE = 'ACTIVE_FREE',
    /** An active customer that has not checked out. Can occur during in-term migration */
    ACTIVE_NOT_CHECKED_OUT = 'ACTIVE_NOT_CHECKED_OUT',
    /** A regular actice customer that has auto collection on, but the credit card removed */
    ACTIVE_CC_REMOVED = 'ACTIVE_CC_REMOVED',
    /** A regular active customer with outstanding invoices */
    ACTIVE_OVERDUE = 'ACTIVE_OVERDUE',
    /** A customer with cancel reason "Not Paid", who fell through dunning */
    DUNNING_FAILED = 'DUNNING_FAILED',
    /** A customer who was manually cancelled (no cancel reason) */
    MANUALLY_CANCELLED = 'MANUALLY_CANCELLED',
    /** A customer with cancel reason "No Card", because they didn't finish their checkout */
    MISSED_ACTIVATION = 'MISSED_ACTIVATION',
    /** Cancelled for some other chargebee reason, this should not normally occur */
    CANCELLED_OTHER = 'CANCELLED_OTHER',
    /** A customer who won't renew at the end of their term */
    NON_RENEWING = 'NON_RENEWING',
    UNKOWN = 'UNKOWN',
}

export enum BILLING_LOCATION {
    EU = 'EU',
    US = 'US',
}

export enum Pricing {
    SUITE_2020 = 'SUITE_2020',
    STARTER_2020 = 'STARTER_2020',
    UNIFIED_2020 = 'UNIFIED_2020',
    UNIFIED_2017 = 'UNIFIED_2017',
    UNIFIED_2015 = 'UNIFIED_2015',
    LEGACY_GOLD = 'LEGACY_GOLD',
    LEGACY_SILVER = 'LEGACY_SILVER',
    LEGACY_BRONZE = 'LEGACY_BRONZE',
    LAUNCH_2024 = 'LAUNCH_2024',
    GROW_2024 = 'GROW_2024',
    ELEVATE_2024 = 'ELEVATE_2024',
}

export interface AddonDto {
    name: string;
    unitPrice: number;
    trialEnd: string;
}

export interface SubscriptionDto {
    id: string;
    status: STATUS;
    cancelReason: CancelReason;
    trialEnd?: string;
    nextBillingAt?: string;
    currentTermStart?: string;
    currentTermEnd?: string;
    startDate?: string;
    activatedAt?: string;
    cancelledAt?: string;
    hasScheduledChanges: boolean;
    settings: SubscriptionSettingsDto;
    scheduledChanges?: SubscriptionSettingsDto;
}

export interface SubscriptionSettingsDto {
    licenseCount: number;
    freeLicenses: number;
    plan: PlanDto;
    addons: AddonDto[];
    couponIds: string[];
    nonProfit: boolean;
}

export interface BillingContact {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
}

export interface BillingDetails {
    email: string;
    firstName: string;
    lastName?: string;
    phoneNumber?: string;
    companyName: string;
    line1?: string;
    line2?: string;
    line3?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    businessCustomerWithoutVatNumber: boolean;
    vatNumber?: string;
    poNumber?: string;
    note: string;
}

export interface PriceInfoDto {
    billingPeriod: number;
    allowedPaymentMethods: PAYMENT_METHOD[];
    licenseCount: number;
    freeLicenses: number;
    freeLimit: number;
    minLicenseCount: number;
    monthsOff: number;
    units: PriceInfoDto.Unit[];
}

export namespace PriceInfoDto {
    export interface Unit {
        name: string;
        units: number;
        price: number;

        periodWithDiscount: number;
        periodWithNonProfitAndDiscount: number;

        monthlyWithDiscount: number;
        monthlyWithNonProfit: number;
        monthlyWithNonProfitAndDiscount: number;
    }
}

export interface PricingDetails {
    licenseCount: number;
    isNonProfit: boolean;
    planId: string;
}

export interface Card {
    status: string;
    firstName: string;
    lastName: string;
    maskedNumber: string;
    cardType: string;
    expiryMonth: string;
    expiryYear: string;
    type: 'VISA' | 'MASTERCARD' | 'AMERICAN_EXPRESS' | 'DISCOVER' | 'JCB' | 'DINERS_CLUB' | 'OTHER' | 'NOT_APPLICABLE';
    // Cards have more props but we don't care about those much in the FE
}

export default interface CustomerDto {
    id: string;
    manuallyCancelled: boolean;
    manuallyCancelledAt?: string;
    subscription: SubscriptionDto;
    billingDetails: BillingDetails;
    card?: Card;
    autoCollected: boolean;
    priceInfo: PriceInfoDto;
    completedCheckout: boolean;
    hasBillingAddress: boolean;
    allowedPaymentMethods: PAYMENT_METHOD[];
    billingLocation: BILLING_LOCATION;
    billingContacts: BillingContact[];
    state: CustomerState;
    hasRequestedSwitchToAnnualSubscription: boolean;
    customerNumber?: string;
}
